import { useEffect, useMemo, useRef, useState } from 'react';
import { buildShoe, hiLoValue } from '../game/engine.js';
import { Card } from '../components/Card.jsx';
import { HintPanel } from '../components/CoachPanel.jsx';

// Drill flow:
// 1. Pick deck size & speed
// 2. App flips cards one at a time at chosen speed (or tap-to-advance in manual mode)
// 3. You keep the running count in your head
// 4. At the end, you enter your final count; app reveals correct answer + per-card breakdown

const SIZES = [
  { label: '5 cards', cards: 5, hint: 'Warmup. Should be effortless.' },
  { label: 'Quarter deck (13)', cards: 13, hint: 'Foundation drill.' },
  { label: 'Half deck (26)', cards: 26, hint: 'Build endurance.' },
  { label: 'Full deck (52)', cards: 52, hint: 'A balanced single deck ends at 0. Use that to self-check.' },
  { label: '2 decks (104)', cards: 104, hint: 'Two balanced decks → still ends at 0.' },
  { label: 'Full shoe (6 decks, 312)', cards: 312, hint: 'Real-game stamina. End count: 0.' },
];

const SPEEDS = [
  { label: 'Manual (tap)', ms: null },
  { label: 'Slow (1.5s)', ms: 1500 },
  { label: 'Medium (1.0s)', ms: 1000 },
  { label: 'Fast (0.6s)', ms: 600 },
  { label: 'Casino fast (0.4s)', ms: 400 },
];

const PHASES = { CONFIG: 'config', RUN: 'run', INPUT: 'input', RESULT: 'result' };

export function CountDrillMode({ drillStats, setDrillStats }) {
  const [phase, setPhase] = useState(PHASES.CONFIG);
  const [size, setSize] = useState(SIZES[1]);
  const [speed, setSpeed] = useState(SPEEDS[2]);
  const [shoe, setShoe] = useState([]);
  const [revealed, setRevealed] = useState([]); // cards already shown
  const [guess, setGuess] = useState('');
  const [result, setResult] = useState(null);
  const timerRef = useRef(null);

  const correctCount = useMemo(() => revealed.reduce((acc, c) => acc + hiLoValue(c.rank), 0), [revealed]);
  const finalCorrect = useMemo(
    () => phase === PHASES.RESULT ? revealed.reduce((acc, c) => acc + hiLoValue(c.rank), 0) : null,
    [phase, revealed],
  );

  function start() {
    const decksNeeded = Math.ceil(size.cards / 52);
    const fresh = buildShoe(decksNeeded);
    // Trim to exact size — buildShoe already shuffles.
    setShoe(fresh.slice(0, size.cards));
    setRevealed([]);
    setGuess('');
    setResult(null);
    setPhase(PHASES.RUN);
  }

  // Auto-advance timer
  useEffect(() => {
    if (phase !== PHASES.RUN || !speed.ms) return undefined;
    if (revealed.length >= shoe.length) {
      setPhase(PHASES.INPUT);
      return undefined;
    }
    timerRef.current = setTimeout(() => {
      setRevealed((r) => [...r, shoe[r.length]]);
    }, speed.ms);
    return () => clearTimeout(timerRef.current);
  }, [phase, revealed.length, shoe, speed.ms]);

  // When manual mode reaches end of deck
  useEffect(() => {
    if (phase === PHASES.RUN && revealed.length === shoe.length && shoe.length > 0) {
      setPhase(PHASES.INPUT);
    }
  }, [phase, revealed.length, shoe.length]);

  function tapNext() {
    if (phase !== PHASES.RUN || speed.ms !== null) return;
    if (revealed.length >= shoe.length) return;
    setRevealed((r) => [...r, shoe[r.length]]);
  }

  function submit() {
    const userCount = parseInt(guess, 10);
    if (Number.isNaN(userCount)) return;
    const error = Math.abs(userCount - correctCount);
    const perfect = error === 0;
    setResult({ userCount, correctCount, error, perfect });
    setPhase(PHASES.RESULT);
    setDrillStats((s) => ({
      runs: (s.runs || 0) + 1,
      perfect: (s.perfect || 0) + (perfect ? 1 : 0),
      totalError: (s.totalError || 0) + error,
      bestStreak: perfect ? Math.max(s.bestStreak || 0, (s.currentStreak || 0) + 1) : (s.bestStreak || 0),
      currentStreak: perfect ? (s.currentStreak || 0) + 1 : 0,
      lastSize: size.cards,
      lastSpeedMs: speed.ms,
    }));
  }

  function reset() {
    setPhase(PHASES.CONFIG);
    setRevealed([]);
    setGuess('');
    setResult(null);
  }

  const accuracy = drillStats.runs > 0 ? Math.round((drillStats.perfect / drillStats.runs) * 100) : 0;
  const avgErr = drillStats.runs > 0 ? (drillStats.totalError / drillStats.runs).toFixed(1) : '—';

  if (phase === PHASES.CONFIG) {
    return (
      <div className="px-4 py-6 max-w-3xl mx-auto flex flex-col gap-6">
        <div className="text-center">
          <h2 className="font-display text-3xl font-bold mb-2">Hi-Lo Count Drill</h2>
          <p className="text-white/70 text-sm">Like running a deck at home, but on your phone.</p>
        </div>

        <HintPanel>
          <b>How Hi-Lo works:</b> tag every card. Cards 2–6 = <b>+1</b>. Cards 7–9 = <b>0</b>. Tens, faces, aces = <b>−1</b>.
          Keep a running tally as cards come out. A balanced deck ends at <b>0</b> — use that to self-check.
        </HintPanel>

        <Field label="Deck size">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {SIZES.map((s) => (
              <button key={s.cards} onClick={() => setSize(s)}
                className={`px-3 py-3 rounded-lg border text-left transition ${size.cards === s.cards ? 'bg-chip text-black border-chip' : 'bg-white/5 border-white/20 hover:bg-white/10'}`}>
                <div className="font-semibold text-sm">{s.label}</div>
                <div className={`text-xs ${size.cards === s.cards ? 'text-black/70' : 'text-white/50'}`}>{s.hint}</div>
              </button>
            ))}
          </div>
        </Field>

        <Field label="Speed">
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
            {SPEEDS.map((sp) => (
              <button key={sp.label} onClick={() => setSpeed(sp)}
                className={`px-3 py-2 rounded-lg border text-sm transition ${speed.label === sp.label ? 'bg-chip text-black border-chip' : 'bg-white/5 border-white/20 hover:bg-white/10'}`}>
                {sp.label}
              </button>
            ))}
          </div>
        </Field>

        <button onClick={start}
          className="px-8 py-4 bg-chip text-black font-bold rounded-lg hover:brightness-110 transition text-lg">
          Start drill
        </button>

        {drillStats.runs > 0 && (
          <div className="text-center text-sm text-white/60 grid grid-cols-2 sm:grid-cols-4 gap-3 bg-black/20 rounded-lg p-3 border border-white/10">
            <DrillStat label="Runs" value={drillStats.runs} />
            <DrillStat label="Perfect" value={`${accuracy}%`} />
            <DrillStat label="Avg error" value={avgErr} />
            <DrillStat label="Best streak" value={drillStats.bestStreak || 0} />
          </div>
        )}
      </div>
    );
  }

  if (phase === PHASES.RUN) {
    const current = revealed[revealed.length - 1];
    const progress = Math.round((revealed.length / shoe.length) * 100);
    return (
      <div className="px-4 py-6 flex flex-col items-center gap-4 select-none" onClick={tapNext}>
        <div className="w-full max-w-md">
          <div className="text-xs text-white/50 mb-1 text-center">{revealed.length} / {shoe.length}</div>
          <div className="h-2 bg-white/10 rounded-full overflow-hidden">
            <div className="h-full bg-chip transition-all" style={{ width: `${progress}%` }} />
          </div>
        </div>

        <div className="h-48 flex items-center justify-center">
          {current ? <Card card={current} size="lg" /> : <div className="text-white/40">Starting…</div>}
        </div>

        {speed.ms === null && (
          <p className="text-white/60 text-sm">Tap anywhere to flip the next card</p>
        )}

        <div className="text-xs text-white/40 mt-4">
          Don't peek — you'll enter your count at the end.
        </div>
      </div>
    );
  }

  if (phase === PHASES.INPUT) {
    return (
      <div className="px-4 py-6 max-w-md mx-auto flex flex-col items-center gap-5">
        <h3 className="font-display text-2xl font-bold">What's your count?</h3>
        <p className="text-white/60 text-sm text-center">
          Enter the running count after all {shoe.length} cards.
        </p>
        <input
          type="number" value={guess}
          onChange={(e) => setGuess(e.target.value)}
          autoFocus
          onKeyDown={(e) => e.key === 'Enter' && submit()}
          className="w-32 text-center text-3xl font-bold py-4 rounded-lg bg-white/10 border-2 border-white/20 focus:border-chip outline-none"
        />
        <button onClick={submit}
          disabled={guess === ''}
          className="px-8 py-3 bg-chip text-black font-bold rounded-lg hover:brightness-110 transition disabled:opacity-40">
          Check answer
        </button>
      </div>
    );
  }

  // RESULT
  return (
    <div className="px-4 py-6 max-w-3xl mx-auto flex flex-col gap-5">
      <div className={`p-6 rounded-lg border text-center ${result.perfect ? 'bg-emerald-900/40 border-emerald-400/40' : 'bg-rose-900/40 border-rose-400/50'}`}>
        <div className="text-5xl font-bold mb-2">{result.perfect ? '✓' : '✗'}</div>
        <div className="text-xl">
          You said <b>{result.userCount}</b> · Correct: <b>{result.correctCount}</b>
        </div>
        {!result.perfect && <div className="text-sm text-white/70 mt-2">Off by {result.error}.</div>}
      </div>

      <details className="bg-black/20 rounded-lg border border-white/10 p-4">
        <summary className="cursor-pointer text-sm text-white/80">See per-card breakdown</summary>
        <div className="grid grid-cols-7 sm:grid-cols-13 gap-1 mt-3">
          {revealed.map((c, i) => {
            const v = hiLoValue(c.rank);
            const color = v > 0 ? 'text-emerald-400' : v < 0 ? 'text-rose-400' : 'text-white/50';
            return (
              <div key={i} className="text-center">
                <div className="text-xs">{c.rank}{c.suit}</div>
                <div className={`text-xs font-bold ${color}`}>{v > 0 ? '+1' : v}</div>
              </div>
            );
          })}
        </div>
      </details>

      <div className="flex gap-3 justify-center">
        <button onClick={start}
          className="px-6 py-3 bg-chip text-black font-bold rounded-lg hover:brightness-110 transition">
          Run again (same settings)
        </button>
        <button onClick={reset}
          className="px-6 py-3 bg-white/10 text-white font-semibold rounded-lg hover:bg-white/20 border border-white/20">
          Change settings
        </button>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest text-white/50 mb-2 block">{label}</label>
      {children}
    </div>
  );
}

function DrillStat({ label, value }) {
  return (
    <div className="flex flex-col">
      <span className="text-[10px] uppercase tracking-wider text-white/50">{label}</span>
      <span className="font-semibold text-white">{value}</span>
    </div>
  );
}
